/**
 * Command Center — Catalysts widget.
 *
 * Renders the LIVE catalyst feature vectors that the Decision Engine already
 * consumes (CAT-1..5) as a scrollable feed. This is the visualization of the
 * architectural mandate: each row is one scored news catalyst with its signed
 * momentum (directional color), confidence, type and affected tickers.
 *
 * Reads purely from the command-center snapshot's `catalysts` quadrant
 * ({ recent: [...], count }) — no extra fetch, no business logic.
 */

import React from 'react';
import { Newspaper, TrendingUp, TrendingDown, Minus } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import type { CommandCenterQuadrant } from '../../lib/api';

/** One scored catalyst feature vector (see core/titan/news_intelligence.py). */
interface CatalystVector {
  article_id?: string;
  title?: string;
  headline?: string;
  source?: string;
  tickers?: string[];
  ticker?: string;
  momentum_score?: number; // signed -100..+100 (+ bullish, - bearish)
  catalyst_type?: string;
  confidence?: number; // 0..1
  scored_at?: string;
}

const relTime = (iso?: string): string => {
  if (!iso) return '';
  try {
    const then = new Date(iso).getTime();
    const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
    if (secs < 60) return `${secs}s ago`;
    const mins = Math.round(secs / 60);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.round(mins / 60);
    return `${hrs}h ago`;
  } catch {
    return '';
  }
};

const tickersOf = (c: CatalystVector): string => {
  const list = c.tickers && c.tickers.length ? c.tickers : c.ticker ? [c.ticker] : [];
  return list.slice(0, 4).join(', ');
};

/** Directional color from signed momentum: green up, red down, muted flat. */
const momentumTone = (score: number): { color: string; Icon: typeof TrendingUp } => {
  if (score > 0) return { color: '#1FC78F', Icon: TrendingUp };
  if (score < 0) return { color: '#E5484D', Icon: TrendingDown };
  return { color: '#6b7280', Icon: Minus };
};

const CatalystRow: React.FC<{ c: CatalystVector }> = ({ c }) => {
  const score = Number(c.momentum_score ?? 0);
  const conf = Math.round(Number(c.confidence ?? 0) * 100);
  const { color, Icon } = momentumTone(score);
  const tickers = tickersOf(c);
  const isPending = (c.catalyst_type ?? '') === 'pending';
  return (
    <div className="flex items-start gap-3 border-b border-border/40 py-2 last:border-b-0">
      <div className="flex w-14 shrink-0 flex-col items-center">
        <Icon className="h-4 w-4" style={{ color }} />
        <span className="text-sm font-bold tabular-nums" style={{ color }}>
          {score > 0 ? '+' : ''}{Math.round(score)}
        </span>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {tickers && <span className="text-sm font-semibold text-foreground">{tickers}</span>}
          <Badge variant={isPending ? 'outline' : 'secondary'} className="text-[10px]">
            {c.catalyst_type || 'unscored'}
          </Badge>
          {!isPending && (
            <span className="text-[11px] text-muted-foreground">{conf}% conf</span>
          )}
        </div>
        <div className="truncate text-xs text-muted-foreground" title={c.title || c.headline || ''}>
          {c.title || c.headline || '(no headline)'}
        </div>
      </div>
      <div className="shrink-0 text-right text-[10px] text-muted-foreground">
        <div>{relTime(c.scored_at)}</div>
        {c.source && <div className="max-w-[80px] truncate">{c.source}</div>}
      </div>
    </div>
  );
};

const CatalystsWidget: React.FC<{ data: CommandCenterQuadrant | undefined }> = ({ data }) => {
  const errored = !!data && typeof data.error === 'string';
  const recent = ((data as Record<string, unknown> | undefined)?.recent as CatalystVector[] | undefined) ?? [];
  const count = Number((data as Record<string, unknown> | undefined)?.count ?? recent.length);

  return (
    <Card className="flex flex-col">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Newspaper className="h-4 w-4" /> News Catalysts
        </CardTitle>
        <Badge variant={errored ? 'destructive' : 'secondary'}>
          {errored ? 'degraded' : `${count} live`}
        </Badge>
      </CardHeader>
      <CardContent className="flex-1">
        {errored ? (
          <div className="text-sm text-destructive">{String(data?.error)}</div>
        ) : recent.length === 0 ? (
          <div className="text-sm text-muted-foreground">No recent catalysts in window.</div>
        ) : (
          <div className="max-h-72 overflow-auto pr-1">
            {recent.map((c, i) => (
              <CatalystRow key={c.article_id || i} c={c} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CatalystsWidget;
